import { Buffer } from "buffer";
import { Dialog } from "../../../components/Dialog";
import LogoDSMarketing from "../../../assets/header/DsMarketing.png";
import Stack from "@mui/material/Stack";
import { useCallback, useMemo, useState } from "react";
import { useStyles } from "../styles";
import Stepper from "@mui/material/Stepper";
import StepConnector from "@mui/material/StepConnector";
import Step from "@mui/material/Step";
import Typography from "@mui/material/Typography";
import TabPanel from "../../../components/TabPanel";
import Arts from "./Tabs/Arts";
import { Images } from "./Tabs/Images";
import { Arts as TArts, Flyer, FlyerDimensions } from "../../../types/arts";
import { ButtonProps } from "@mui/material/Button";
import { dimensionsImage } from "../../../lib/dimensions";
import { useAws } from "../../../hooks/useAws";
import { FileToBase64 } from "../../../lib/toBase64";
import { useAuth } from "../../../hooks/useAuth";
import Avatar from "@mui/material/Avatar";

type ArtsDialogProps = {
  open: boolean;
  onClose: () => void;
  onChange: (data: FlyerDimensions) => void;
};

export function ArtsDialog({ open, onClose, onChange }: ArtsDialogProps) {
  const { uploadS3 } = useAws();
  const { user } = useAuth();
  const styles = useStyles();

  const [uploading, setUploading] = useState<boolean>(false);
  const [artSelected, setArtSelected] = useState<TArts | null>();
  const [currentStep, setCurrentStep] = useState<number>(0);

  const steps = useMemo(() => {
    return ["1. Escolha uma arte", "2. Escolha uma imagem"];
  }, []);

  const handleSelectedArt = useCallback((data: TArts) => {
    setCurrentStep((prevState) => prevState + 1);
    setArtSelected(data);
  }, []);

  const handleSelectedImage = useCallback(
    async (data: Flyer) => {
      const response = await dimensionsImage(data.image_url);

      onChange({
        height: response.height,
        url: response.url,
        width: response.width,
      });
    },
    [onChange]
  );

  const handleUpload = useCallback(
    async (file: File) => {
      try {
        setUploading(true);
        const base64 = await FileToBase64(file);
        const fileSplit = base64.split(";base64,")[1];
        const objectUploadData = {
          file: Buffer.from(fileSplit, "base64"),
          folderName: "crm",
          bucketName: "datasalesio-imagens",
          subFolderName: `${user.companyId}/`,
          distinctName: true,
          name: file.name,
          type: file.type,
          extension: file.name.split(".").pop(),
        };
        const url = await uploadS3(objectUploadData);
        const response = await dimensionsImage(url);

        onChange({
          height: response.height,
          url: response.url,
          width: response.width,
        });
      } catch (err) {
        console.log(err);
      } finally {
        setUploading(false);
      }
    },
    [user.companyId, uploadS3, onChange]
  );

  const handleGoBack = useCallback(() => {
    setCurrentStep((prevState) => prevState - 1);
  }, []);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Voltar",
        onClick: handleGoBack,
        disabled: currentStep === 0,
      },
    ];
  }, [currentStep, handleGoBack]);

  return (
    <Dialog
      data-testid="template-content-dialog-select-image"
      maxWidth="lg"
      fullWidth
      title={
        currentStep > 0 ? artSelected.nome_tabloide_digital : "Enviar imagem"
      }
      open={open}
      onClose={onClose}
      actions={actions}
    >
      <Stack
        className={styles.stackStepper}
        direction="row"
        justifyContent="center"
      >
        <Stepper
          nonLinear
          className={styles.rootStepper}
          connector={<StepConnector classes={{ line: styles.lineStepper }} />}
        >
          {steps.map((label) => (
            <Step key={label}>
              <Typography fontWeight={500} variant="body2" color="primary">
                {label}
              </Typography>
            </Step>
          ))}
        </Stepper>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Avatar
          id="avatar"
          className={styles.icon}
          src={LogoDSMarketing}
          sx={{ width: 24, height: 24 }}
          variant="rounded"
        />
        <Typography fontWeight={500}>DS Marketing</Typography>
      </Stack>
      <TabPanel index={0} value={currentStep}>
        <Arts
          uploading={uploading}
          onSelect={handleSelectedArt}
          onUpload={handleUpload}
        />
      </TabPanel>
      <TabPanel index={1} value={currentStep}>
        <Images art={artSelected} onSelect={handleSelectedImage} />
      </TabPanel>
    </Dialog>
  );
}
